// Story mode, act one (off unless the player opened the page with ?story=1). Beats live in common/data/story.json;
// the server (server/src/story.ts) decides every outcome, the client only shows the line and the pointer.
import storyJ from '../data/story.json';
import { VEHICLES, WORLD } from './data';

export interface StoryBeat { id: string; text: string; target: { poi?: string; plaza?: string }; point: string }
export const STORY = storyJ as unknown as {
  act1: StoryBeat[];
  endText: string;
  moveHint: { kb: string; pad: string; touch: string };
  garage: {
    car: { type: keyof typeof VEHICLES; x: number; y: number; angle: number; hpMult: number };
    deliverRadiusPx: number;
    ambush: { base: number; levelStep: number; min: number; max: number; maxAlive: number; ring: number; cleanupSec: number };
  };
};
export const GARAGE_YARD = 'Rust Lotus yard';
export const BEAT_ORDER = ['intro.courier', 'intro.theft', 'intro.gear', 'garage.job', 'garage.steal', 'garage.deliver'];
type StoryChar = { story?: { done: string[]; run?: number } };
export const storyDone = (c: StoryChar, id: string) => !!c.story?.done.includes(id);
/** The first act-one beat the character has not finished, or null. */
export const currentBeat = (c: StoryChar): StoryBeat | null => STORY.act1.find(b => !storyDone(c, b.id)) ?? null;
export const beatById = (id: string) => STORY.act1.find(b => b.id === id)!;
/** Ambush size for a character level: base + floor(level / levelStep), clamped to [min, max]. */
export const ambushCount = (level: number) => { const a = STORY.garage.ambush; return Math.max(a.min, Math.min(a.max, a.base + Math.floor(Math.max(1, level) / a.levelStep))); };

export function validateStory(e: string[]) {
  const ids = new Set<string>();
  if (STORY.act1?.map(b => b.id).join() !== BEAT_ORDER.join()) e.push(`story.act1 must be ${BEAT_ORDER.join(', ')} in that order`);
  for (const k of ['kb', 'pad', 'touch'] as const) if (!STORY.moveHint?.[k]) e.push(`story.moveHint.${k} is empty`);
  if (!STORY.endText || STORY.endText.length > 120) e.push('story.endText needs 1-120 characters');
  if (!STORY.act1?.length) e.push('story.act1 is empty');
  for (const b of STORY.act1 ?? []) {
    const p = `story.${b.id}`;
    if (!b.id || ids.has(b.id)) e.push(`${p} id missing or duplicate`);
    ids.add(b.id);
    if (!b.text || b.text.length > 120) e.push(`${p} text needs 1-120 characters`);
    if (!b.point || b.point.length > 12) e.push(`${p} point label needs 1-12 characters`);
    const t = b.target ?? {};
    if (t.poi !== undefined ? !WORLD.pois.some(o => o.id === t.poi) : t.plaza === undefined || !WORLD.plazas.some(z => z.name === t.plaza)) e.push(`${p} target must name a known poi or plaza`);
  }
  const g = STORY.garage, yard = WORLD.plazas.find(z => z.name === GARAGE_YARD);
  if (!VEHICLES[g?.car?.type]) e.push('story.garage.car.type is not a vehicle');
  else if (!yard || g.car.x < yard.x || g.car.y < yard.y || g.car.x >= yard.x + yard.w || g.car.y >= yard.y + yard.h) e.push('story.garage.car must sit inside the Rust Lotus yard');
  if (!(g?.car?.hpMult >= 1 && g.car.hpMult <= 4)) e.push('story.garage.car.hpMult must be 1-4');
  if (!(g?.deliverRadiusPx >= 32 && g.deliverRadiusPx <= 300)) e.push('story.garage.deliverRadiusPx must be 32-300');
  const a = g?.ambush;
  if (!a || !(a.min >= 1 && a.max >= a.min && a.max <= 6) || !(a.base >= 0) || !(a.levelStep >= 1) || !(a.maxAlive >= a.max && a.maxAlive <= 6) || !(a.ring >= 24) || !(a.cleanupSec >= 0)) e.push('story.garage.ambush needs min 1+, max >= min (max 6), base >= 0, levelStep >= 1, maxAlive >= max (max 6), ring >= 24, cleanupSec >= 0');
}
